import { FC, ReactElement, useEffect } from 'react'
import { useSelector } from 'react-redux'

import { AbsenceFormData } from '../../../@paco/entities/Absence/Absence'
import { transformLegacyAbsenceToAbsence } from '../../../@paco/entities/Absence/AbsenceTransformers'
import { PayrollPeriod } from '../../../@paco/entities/PayrollPeriod/PayrollPeriod'
import { patchAbsence } from '../../../@paco/redux/absences/absencesActions'
import { setError as setAbsencesError } from '../../../@paco/redux/absences/absencesReducer'
import { useTypedDispatch, useTypedSelector } from '../../../@paco/redux/store'
import { setToast } from '../../../@paco/redux/toasts/toastsReducer'
import { ToastType } from '../../../@paco/types'

import { CompletePeriodAbsencesContainer } from '../../../containers'
import { Absence, WeekWithHours } from '../../../models'
import { Reducers } from '../../../redux/reducers'
import {
    transformPayrollPeriodToLegacyPayrollPeriod,
} from '../../../services/PayrollPeriodService/transformPayrollPeriodLegacy'

interface ConnectedCompletePeriodAbsencesProps {
    activePeriod?: PayrollPeriod;
    className?: string;
}

const ConnectedCompletePeriodAbsences: FC<ConnectedCompletePeriodAbsencesProps> = ({ activePeriod, className }): ReactElement => {
    const dispatch = useTypedDispatch()

    const isAbsencesLoading = useSelector<Reducers, boolean>(state => state.absencesReducerToolkit.isLoading)
    const absences = useSelector<Reducers, Absence[]>(state => state.absencesReducerToolkit.absences)
    const { permissions, role: currentUserRole } = useTypedSelector(state => state.authenticatedUserReducer)
    const { payrollPeriods } = useTypedSelector(state => state.pacoPayrollPeriodsReducer)
    const { isLoading: isPacoAbsencesLoading, error: absencesError } = useTypedSelector(state => state.pacoAbsencesReducer)

    const isLoading = isAbsencesLoading || isPacoAbsencesLoading

    const handleSubmitAbsence = async (
        absence: Absence,
        data: AbsenceFormData,
        weeksWithHours: WeekWithHours[],
    ): Promise<void> => {
        const legacyPayrollPeriod = activePeriod ? transformPayrollPeriodToLegacyPayrollPeriod(activePeriod) : undefined

        if (legacyPayrollPeriod) {
            await dispatch(patchAbsence(
                transformLegacyAbsenceToAbsence(absence),
                data,
                absence.absenceHours || [],
                weeksWithHours,
            ))
        }
    }

    useEffect(() => {
        if (absencesError) {
            dispatch(setToast({
                text: absencesError,
                type: ToastType.fail,
                shouldFadeOutAutomatically: false,
            }))

            dispatch(setAbsencesError(''))
        }
    }, [absencesError])

    return (
        <CompletePeriodAbsencesContainer
            isLoading={isLoading}
            isPeriodLocked={activePeriod?.isLocked}
            permissions={permissions}
            currentUserRole={currentUserRole}
            absences={absences}
            payrollPeriods={payrollPeriods}
            onSubmitAbsence={handleSubmitAbsence}
            className={className}
        />
    )
}

export default ConnectedCompletePeriodAbsences
